import { useState, useCallback } from 'react';
import { ProjectGenerationRequest, ProjectGenerationState, ProjectGenerationTask, ProjectGenerationStatus, ProjectProcessingStatus } from '@/types/project-generation-types';
import { useWorkbookOperations } from './use-workbook-operations';
import { useAddGoalMutation } from '@/hooks/goal-planner/mutations/use-add-goal-mutation';
import { useGoalWorkbookJunction } from '@/hooks/goal-planner/use-goal-workbook-junction';
import { useProjectGeneration } from './use-project-generation';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';
import { supabase } from '@/integrations/supabase/client';

const initialTasks: ProjectGenerationTask[] = [
  { id: 'generation', name: 'Generating Project', status: 'pending' },
  { id: 'workspace', name: 'Linking AI Agent', status: 'pending' },
  { id: 'goals', name: 'Setting Up Goals', status: 'pending' },
  { id: 'finalizing', name: 'Finalizing Project', status: 'pending' },
];

const initialState: ProjectGenerationState = {
  status: 'idle',
  progress: 0,
  tasks: initialTasks,
  currentTaskDescription: '',
  error: null,
  generatedProjectId: null,
  generatedGoalId: null,
};

export const useProjectCreation = () => {
  const [state, setState] = useState<ProjectGenerationState>(initialState);
  const { createWorkbook } = useWorkbookOperations();
  const { mutateAsync: createGoal } = useAddGoalMutation();
  const { createJunction } = useGoalWorkbookJunction();
  const { generateProjectData } = useProjectGeneration();

  const updateTaskStatus = useCallback((taskId: string, status: ProjectGenerationStatus) => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(task => 
        task.id === taskId ? { ...task, status } : task
      )
    }));
  }, []);

  const startCreation = useCallback(async (request: ProjectGenerationRequest) => {
    setState({
      status: 'processing',
      progress: 0,
      tasks: initialTasks.map(t => ({...t, status: 'pending'})),
      currentTaskDescription: 'Starting project creation...',
      error: null,
      generatedProjectId: null,
      generatedGoalId: null,
    });

    try {
      // Step 1: Generate Project Plan (25% progress)
      setState(prev => ({ ...prev, progress: 10, currentTaskDescription: 'Analyzing your vision and generating intelligent project structure...' }));
      updateTaskStatus('generation', 'in-progress');
      
      const projectData = await generateProjectData({ userGoal: request.userGoal });
      
      updateTaskStatus('generation', 'completed');
      setState(prev => ({ ...prev, progress: 25, currentTaskDescription: 'Project plan generated successfully! Linking AI agent...' }));

      // Step 2: Linking AI Agent (50% progress)
      updateTaskStatus('workspace', 'in-progress');
      
      const workbook = await createWorkbook({
        title: request.userGoal, // FIXED: Use user's actual project name
        metadata: {
          originalUserInput: request.userGoal,
          aiGenerated: {
            title: projectData.title, // Store AI suggestion as metadata
            goal: projectData.goal,
            description: projectData.description,
            imagePrompt: projectData.imagePrompt,
            followUpQuestions: projectData.followUpQuestions,
            generatedAt: new Date().toISOString()
          }
        }
      });

      // 🎯 COVER IMAGE GENERATION: Fire-and-forget during project creation
      if (projectData.imagePrompt) {
        console.log('[useProjectCreation] Triggering cover image generation during creation');
        try {
          // Import the utility function dynamically to avoid circular dependencies
          const { generateCoverImageAndSave } = await import('@/utils/auto-cover-image-generation');
          
          // Fire-and-forget: Generate cover image in background without blocking project creation
          generateCoverImageAndSave(workbook.id, projectData.imagePrompt)
            .then((imageUrl) => {
              if (imageUrl) {
                console.log('[useProjectCreation] Cover image generated successfully during creation:', imageUrl);
              }
            })
            .catch((error) => {
              console.warn('[useProjectCreation] Cover image generation failed (non-critical):', error);
            });
        } catch (error) {
          // Silent failure - never block project creation for cover image issues
          console.warn('[useProjectCreation] Cover image generation setup failed (non-critical):', error);
        }
      }

      updateTaskStatus('workspace', 'completed');
      setState(prev => ({ ...prev, progress: 50, currentTaskDescription: 'AI agent linked successfully! Setting up goals...' }));

      // Step 3: Setting Up Goals (75% progress)
      updateTaskStatus('goals', 'in-progress');
      
      const goal = await createGoal({
        title: projectData.goal, // Keep AI-generated goal (this is strategic/specific)
        description: projectData.description
      });

      // Create junction record linking them with agent thread
      await createJunction({
        goalId: goal.id,
        workbookId: workbook.id,
        agentThreadId: goal.agentThreadId!
      });

      updateTaskStatus('goals', 'completed');
      setState(prev => ({ ...prev, progress: 75, currentTaskDescription: 'Goals configured successfully! Finalizing project...' }));

      // Step 4: Finalizing Project (100% progress)
      updateTaskStatus('finalizing', 'in-progress');
      
      // Update agent thread title with USER'S project title
      if (goal.agentThreadId) {
        try {
          await supabase
            .from('agent_threads')
            .update({ 
              title: `Chat: ${request.userGoal}`, // FIXED: Use user's project name
              updated_at: new Date().toISOString()
            })
            .eq('id', goal.agentThreadId);
        } catch (titleUpdateError) {
          console.warn('[useProjectCreation] Failed to update agent thread title:', titleUpdateError);
          // Non-critical error, continue with project creation
        }
      }

      updateTaskStatus('finalizing', 'completed');

      setState(prev => ({ 
        ...prev, 
        status: 'success',
        progress: 100,
        currentTaskDescription: 'Project created successfully!',
        generatedProjectId: workbook.id,
        generatedGoalId: goal.id
      }));

      // Trigger quest completion for project creation (Step 4)
      QuestTriggers.projectCreated();

    } catch (error: any) {
      console.error('[useProjectCreation] Error:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Failed to create project. Please try again.',
        currentTaskDescription: 'An error occurred during project creation.'
      }));
    }
  }, [generateProjectData, createWorkbook, createGoal, createJunction, updateTaskStatus]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    startCreation,
    reset
  };
}; 